import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { selectProjects } from "../../redux/work/work.selectors";

import "./netflix-project.styles.css";

const isVideoUrl = (url) =>
  url && /\.(mp4|webm|ogv)$/i.test(url);

const getHeroVideoSrc = (project) => {
  if (project?.videoUrl && isVideoUrl(project.videoUrl)) return project.videoUrl;
  if (project?.bgUrl && isVideoUrl(project.bgUrl)) return project.bgUrl;
  return null;
};

const getHeroImageSrc = (project) => {
  if (project?.bgUrl && !isVideoUrl(project.bgUrl)) return project.bgUrl;
  return null;
};

const getProgressPercent = (id) => {
  const seed = String(id)
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return 35 + (seed % 55);
};

const ProjectThumbnail = ({ project }) => {
  const hasVideo = project.bgUrl && isVideoUrl(project.bgUrl);
  const hasImage = project.bgUrl && !isVideoUrl(project.bgUrl);
  const progress = getProgressPercent(project.id);

  return (
    <div className="netflix-project-card-wrapper">
      <a
        href={project.link}
        target="_blank"
        rel="noreferrer"
        className="netflix-thumbnail-card"
        aria-label={project.title}
      >
        {hasVideo ? (
          <video autoPlay loop muted playsInline className="thumbnail-media">
            <source src={project.bgUrl} type="video/mp4" />
          </video>
        ) : hasImage ? (
          <div
            className="thumbnail-media thumbnail-image"
            style={{ backgroundImage: `url(${project.bgUrl})` }}
          />
        ) : (
          <div className="thumbnail-media thumbnail-placeholder">
            <span>{project.title}</span>
          </div>
        )}

        <div className="thumbnail-hover-panel">
          <p className="thumbnail-header">{project.header}</p>
          <h3 className="thumbnail-title">{project.title}</h3>
        </div>

        <div className="watch-progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
      </a>
    </div>
  );
};

const NetflixStyleProjectSection = ({ projects }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [activeDemoUrl, setActiveDemoUrl] = useState(null);
  const heroVideoRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!projects || projects.length === 0) return null;

  const heroProject = projects[0];
  const sliderProjects = projects.slice(1,3);
  const heroVideoSrc = getHeroVideoSrc(heroProject);
  const heroImageSrc = getHeroImageSrc(heroProject);

  const toggleMute = () => {
    if (heroVideoRef.current) {
      heroVideoRef.current.muted = !heroVideoRef.current.muted;
      setIsMuted(heroVideoRef.current.muted);
    }
  };

  const handlePlayDemo = (e) => {
    e.preventDefault();
    if (heroVideoSrc) {
      setActiveDemoUrl(heroVideoSrc);
    }
  };

  const handleCloseDemo = (e) => {
    e.preventDefault();
    setActiveDemoUrl(null);
  };

  return (
    <div className={`netflix-portfolio ${isScrolled ? "page-scrolled" : ""}`}>
      <nav className="netflix-nav">
        <div className="netflix-nav-left">
          <Link to="/" className="netflix-logo">
            PANKAJ<span className="logo-accent"> </span>VYAS
          </Link>
          <ul className="netflix-nav-links">
            <li>
              <Link to="/">Home</Link>
            </li>
            <li>
              <Link to="/projects" className="active">
                Projects
              </Link>
            </li>
            <li>
              <Link to="/about">About</Link>
            </li>
            <li>
              <Link to="/contact">Contact</Link>
            </li>
          </ul>
        </div>
      </nav>

      <header className="billboard-hero">
        <div className="billboard-media-container">
          {heroVideoSrc ? (
            <video
              ref={heroVideoRef}
              autoPlay
              loop
              muted
              playsInline
              className="billboard-video-bg"
            >
              <source src={heroVideoSrc} type="video/mp4" />
            </video>
          ) : heroImageSrc ? (
            <div
              className="billboard-bg-fallback"
              style={{ backgroundImage: `url(${heroImageSrc})` }}
            />
          ) : (
            <div className="billboard-bg-fallback billboard-bg-empty" />
          )}
          <div className="vignette-left" />
          <div className="vignette-bottom" />
        </div>

        <div className="billboard-content">
          <p className="project-brand">{heroProject.header}</p>
          <h1 className="project-title">{heroProject.title}</h1>
          {heroProject.description?.[0] && (
            <p className="project-tagline">{heroProject.description[0]}</p>
          )}

          <div className="billboard-buttons">
            {heroVideoSrc && (
              <button
                type="button"
                className="btn-netflix play"
                onClick={handlePlayDemo}
              >
                <span className="btn-icon">▶</span> Play Demo
              </button>
            )}
            {heroProject.link && (
              <a
                href={heroProject.link}
                target="_blank"
                rel="noreferrer"
                className="btn-netflix info"
              >
                <span className="btn-icon">ⓘ</span>{" "}
                {heroProject.btnText || "More Info"}
              </a>
            )}
          </div>
        </div>

        <div className="billboard-meta">
          {heroVideoSrc && (
            <button
              type="button"
              className="volume-toggle"
              onClick={toggleMute}
              aria-label={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? "🔇" : "🔊"}
            </button>
          )}
        </div>
      </header>

      <main className="slider-rows-container">
        {sliderProjects.length > 0 && (
          <section className="project-row">
            <h2 className="row-heading">More Projects</h2>
            <div className="netflix-slider-track">
              {sliderProjects.map((project) => (
                <ProjectThumbnail key={project.id} project={project} />
              ))}
            </div>
          </section>
        )}

        <section className="project-row">
          <h2 className="row-heading">All Projects</h2>
          <div className="netflix-slider-track">
            {projects.map((project) => (
              <ProjectThumbnail key={`all-${project.id}`} project={project} />
            ))}
          </div>
        </section>
      </main>

      {activeDemoUrl && (
        <div className="demo-video-overlay">
          <div className="demo-video-container">
            <video className="demo-video-player" controls autoPlay>
              <source src={activeDemoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <button
              type="button"
              className="demo-close-btn"
              onClick={handleCloseDemo}
              aria-label="Close demo"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  projects: selectProjects,
});

export default connect(mapStateToProps)(NetflixStyleProjectSection);
